import React, { useState, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { promptApi } from '@/services/promptApi';
import { useEntity } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft,
  Play,
  MessageSquare,
  RefreshCw,
  User,
  Beaker,
  Layers,
  FileText,
} from 'lucide-react';
import { LoadingState, ErrorState, CopyableId, BrandedSpinner } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type {
  PromptDetail,
  PromptVariable,
  PromptParameter,
  RunPromptResponse,
} from '@/types/prompt';
import { cn } from '@/lib/utils';

function extractOutputText(runResult: RunPromptResponse | null): string | null {
  if (!runResult) return null;
  const r = runResult as Record<string, unknown>;
  if (typeof r.output === 'string') return r.output;
  if (typeof r.result === 'string') return r.result;
  if (typeof r.text === 'string') return r.text;
  return JSON.stringify(runResult, null, 2);
}

export const PromptDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const openTestPanel = searchParams.get('test') === '1';
  const defaultTab = openTestPanel ? 'test' : 'overview';

  const [activeTab, setActiveTab] = useState(defaultTab);

  const [testVariables, setTestVariables] = useState<Record<string, string>>({});
  const [runResult, setRunResult] = useState<RunPromptResponse | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const [userIdentifier, setUserIdentifier] = useState('');
  const [userVariables, setUserVariables] = useState<Record<string, string>>({});
  const [userRunResult, setUserRunResult] = useState<RunPromptResponse | null>(null);
  const [userRunError, setUserRunError] = useState<string | null>(null);
  const [isUserRunning, setIsUserRunning] = useState(false);

  const [customPromptText, setCustomPromptText] = useState('');
  const [customRunResult, setCustomRunResult] = useState<RunPromptResponse | null>(null);
  const [customRunError, setCustomRunError] = useState<string | null>(null);
  const [isCustomRunning, setIsCustomRunning] = useState(false);

  const [releaseTag, setReleaseTag] = useState('');
  const [fullResyncReleaseTag, setFullResyncReleaseTag] = useState('');
  const [resyncMessage, setResyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isResyncing, setIsResyncing] = useState(false);
  const [isFullResyncing, setIsFullResyncing] = useState(false);

  const {
    data: prompt,
    isLoading,
    error,
    refetch,
  } = useEntity<PromptDetail>({
    fetchFn: (keyOrId: string) => promptApi.get(keyOrId),
    entityId: id ?? undefined,
    entityName: 'prompt',
    enabled: !!id,
  });

  const variables = useMemo((): PromptVariable[] => {
    const params = prompt?.parameters ?? prompt?.variables;
    if (!params || !Array.isArray(params)) return [];
    return params.map((p: PromptVariable | PromptParameter) => ({
      name: p.name,
      type: p.type,
      description: p.description,
      required: p.required,
      default: 'default' in p ? p.default : undefined,
    }));
  }, [prompt?.parameters, prompt?.variables]);

  const handleVariableChange = (
    setter: React.Dispatch<React.SetStateAction<Record<string, string>>>,
    name: string,
    value: string
  ) => {
    setter((prev) => ({ ...prev, [name]: value }));
  };

  const handleRun = async () => {
    if (!prompt) return;
    const docId = prompt.docId ?? prompt.id;
    if (!docId) return;
    setRunError(null);
    setRunResult(null);
    setIsRunning(true);
    try {
      const result = await promptApi.run(docId, {
        variables: Object.keys(testVariables).length ? testVariables : undefined,
      });
      setRunResult(result);
    } catch (e) {
      const err = e as { message?: string };
      setRunError(err?.message ?? 'Failed to run prompt');
    } finally {
      setIsRunning(false);
    }
  };

  const handleRunWithUser = async () => {
    const docId = prompt?.docId ?? prompt?.id;
    if (!docId) return;
    setUserRunError(null);
    setUserRunResult(null);
    setIsUserRunning(true);
    try {
      const body = {
        userId: userIdentifier.trim() || undefined,
        userEmail: userIdentifier.trim() || undefined,
        variables: Object.keys(userVariables).length ? userVariables : undefined,
      };
      const result = await promptApi.runWithUser(docId, body);
      setUserRunResult(result);
    } catch (e) {
      const err = e as { message?: string };
      setUserRunError(err?.message ?? 'Failed to run prompt with user context');
    } finally {
      setIsUserRunning(false);
    }
  };

  const handleResyncThis = async () => {
    const docId = prompt?.docId ?? prompt?.id;
    if (!docId) return;
    setResyncMessage(null);
    setIsResyncing(true);
    try {
      await promptApi.resync(docId, releaseTag ? { releaseTag: releaseTag.trim() } : undefined);
      setResyncMessage({ type: 'success', text: 'This prompt has been resynced successfully.' });
      refetch();
    } catch (e) {
      const err = e as { message?: string };
      setResyncMessage({
        type: 'error',
        text: err?.message ?? 'Failed to resync this prompt.',
      });
    } finally {
      setIsResyncing(false);
    }
  };

  const handleFullResyncWithReleaseId = async () => {
    setResyncMessage(null);
    setIsFullResyncing(true);
    try {
      await promptApi.fullResync(
        fullResyncReleaseTag.trim() ? { releaseTag: fullResyncReleaseTag.trim() } : undefined
      );
      setResyncMessage({ type: 'success', text: 'Entire prompt library has been resynced successfully.' });
      setFullResyncReleaseTag('');
      refetch();
    } catch (e) {
      const err = e as { message?: string };
      setResyncMessage({
        type: 'error',
        text: err?.message ?? 'Failed to resync entire library.',
      });
    } finally {
      setIsFullResyncing(false);
    }
  };

  const handleRunCustomPrompt = async () => {
    const docId = prompt?.docId ?? prompt?.id;
    if (!docId) return;
    setCustomRunError(null);
    setCustomRunResult(null);
    setIsCustomRunning(true);
    try {
      const result = await promptApi.run(docId, {
        variables: customPromptText.trim()
          ? { custom_prompt: customPromptText.trim(), user_prompt: customPromptText.trim() }
          : undefined,
      });
      setCustomRunResult(result);
    } catch (e) {
      const err = e as { message?: string };
      setCustomRunError(err?.message ?? 'Failed to run prompt');
    } finally {
      setIsCustomRunning(false);
    }
  };

  const outputText = useMemo(() => extractOutputText(runResult), [runResult]);
  const userOutputText = useMemo(() => extractOutputText(userRunResult), [userRunResult]);
  const customOutputText = useMemo(() => extractOutputText(customRunResult), [customRunResult]);

  if (isLoading) {
    return <LoadingState message="Loading prompt..." />;
  }

  if (error || !prompt) {
    return (
      <ErrorState
        error={error ?? new Error('Prompt not found')}
        onRetry={refetch}
      />
    );
  }

  const systemPrompt = (prompt.system_prompt ?? '') as string;
  const userPromptTemplate = (prompt.user_prompt_template ?? '') as string;
  const templateBody =
    (prompt.template ?? prompt.templateBody ?? prompt.body ?? prompt.content ?? '') as string;
  const hasTemplateContent = Boolean(systemPrompt || userPromptTemplate || templateBody);
  const hasVariables = variables.length > 0;

  const displayDate = (value: string | undefined) =>
    value ? formatDate(value) : '—';
  const updatedOrModified =
    prompt.updatedAt ?? prompt.lastModified ?? prompt.createdAt;

  const statusColor =
    prompt.status === 'PUBLISHED'
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      : prompt.status === 'DRAFT'
        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
        : 'bg-muted text-muted-foreground';

  return (
    <div className="space-y-6">
      {/* Header: back, breadcrumb, title, badges, primary action */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4 min-w-0">
          <Button variant="ghost" size="icon" className="shrink-0 mt-0.5" asChild>
            <Link to="/admin/prompts">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">Prompt Library</p>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight mt-0.5 truncate">
              {prompt.name ?? prompt.key ?? 'Prompt'}
            </h1>
            {prompt.description && (
              <p className="text-muted-foreground mt-1.5 line-clamp-2">
                {prompt.description}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {prompt.status != null && String(prompt.status).trim() !== '' && (
                <span
                  className={cn(
                    'px-2.5 py-0.5 rounded-full text-xs font-medium',
                    statusColor
                  )}
                >
                  {String(prompt.status)}
                </span>
              )}
              {prompt.category != null && String(prompt.category).trim() !== '' && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                  {String(prompt.category)}
                </span>
              )}
              {Array.isArray(prompt.tags) && prompt.tags.length > 0 && (
                <>
                  {prompt.tags.slice(0, 5).map((tag: string) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded-md text-xs bg-muted/80 text-muted-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                  {prompt.tags.length > 5 && (
                    <span className="text-xs text-muted-foreground">
                      +{prompt.tags.length - 5} more
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <Button
          onClick={() => setActiveTab('test')}
          size="default"
          className="shrink-0"
        >
          <Play className="mr-2 h-4 w-4" />
          Test prompt
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-flex">
          <TabsTrigger value="overview" className="gap-2">
            <MessageSquare className="h-4 w-4 shrink-0" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="test" className="gap-2">
            <Beaker className="h-4 w-4 shrink-0" />
            Test
          </TabsTrigger>
          <TabsTrigger value="write-test" className="gap-2">
            <FileText className="h-4 w-4 shrink-0" />
            Write & test
          </TabsTrigger>
          <TabsTrigger value="test-with-user" className="gap-2">
            <User className="h-4 w-4 shrink-0" />
            Test with user
          </TabsTrigger>
          <TabsTrigger value="resync" className="gap-2">
            <RefreshCw className="h-4 w-4 shrink-0" />
            Resync
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Basic information</CardTitle>
                <CardDescription>Identification and configuration</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {prompt.docId != null && (
                  <CopyableId id={String(prompt.docId)} label="Doc ID" />
                )}
                <CopyableId
                  id={String(prompt.id ?? prompt.key ?? '—')}
                  label="Key"
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  {(prompt.version != null && String(prompt.version).trim() !== '') && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Version</label>
                      <p className="mt-1">{String(prompt.version)}</p>
                    </div>
                  )}
                  {prompt.model != null && String(prompt.model).trim() !== '' && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Model</label>
                      <p className="mt-1 font-mono text-sm">{String(prompt.model)}</p>
                    </div>
                  )}
                  {prompt.temperature != null && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Temperature</label>
                      <p className="mt-1">{Number(prompt.temperature)}</p>
                    </div>
                  )}
                  {prompt.author != null && String(prompt.author).trim() !== '' && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Author</label>
                      <p className="mt-1">{String(prompt.author)}</p>
                    </div>
                  )}
                </div>
                <div className="grid gap-4 sm:grid-cols-2 pt-2 border-t">
                  {updatedOrModified && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Last updated</label>
                      <p className="mt-1">{displayDate(updatedOrModified)}</p>
                    </div>
                  )}
                  {prompt.createdAt && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Created</label>
                      <p className="mt-1">{displayDate(prompt.createdAt)}</p>
                    </div>
                  )}
                </div>
                {prompt.metadata != null && Object.keys(prompt.metadata).length > 0 && (
                  <div className="pt-2 border-t">
                    <label className="text-sm font-medium text-muted-foreground">Metadata</label>
                    <pre className="mt-2 rounded-lg border bg-muted/50 p-3 text-xs overflow-x-auto whitespace-pre-wrap font-mono max-h-[160px] overflow-y-auto">
                      {JSON.stringify(prompt.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>

            {variables.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Parameters</CardTitle>
                  <CardDescription>
                    Inputs used when running this prompt in the Test tab
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    {variables.map((v: PromptVariable) => (
                      <li
                        key={v.name}
                        className="flex flex-col gap-1 rounded-lg border bg-muted/30 px-3 py-2.5"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-sm font-medium">{v.name}</span>
                          {v.type != null && (
                            <span className="text-xs text-muted-foreground">({v.type})</span>
                          )}
                          {v.required && (
                            <span className="text-xs font-medium text-destructive">Required</span>
                          )}
                        </div>
                        {v.description != null && (
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {v.description}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {(hasTemplateContent || templateBody) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Prompt templates</CardTitle>
                <CardDescription>
                  System instructions and user template with placeholders
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {systemPrompt && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">System prompt</h4>
                    <pre className="rounded-lg border bg-muted/50 p-4 text-sm overflow-x-auto whitespace-pre-wrap font-mono max-h-[280px] overflow-y-auto">
                      {systemPrompt}
                    </pre>
                  </div>
                )}
                {userPromptTemplate && (
                  <div className={systemPrompt ? 'pt-4 border-t' : ''}>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">User prompt template</h4>
                    <pre className="rounded-lg border bg-muted/50 p-4 text-sm overflow-x-auto whitespace-pre-wrap font-mono max-h-[380px] overflow-y-auto">
                      {userPromptTemplate}
                    </pre>
                  </div>
                )}
                {!systemPrompt && !userPromptTemplate && templateBody && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Template body</h4>
                    <pre className="rounded-lg border bg-muted/50 p-4 text-sm overflow-x-auto whitespace-pre-wrap font-mono max-h-[380px] overflow-y-auto">
                      {templateBody}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="test" className="mt-6">
          <Card className={cn(openTestPanel && 'ring-2 ring-primary')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Beaker className="h-5 w-5" />
                Test prompt
              </CardTitle>
              <CardDescription>
                Fill in the parameters below and run to see the model output. Required fields are marked with *.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {hasVariables && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium">Parameters</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Provide values for the placeholders used in this prompt.
                    </p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {variables.map((v: PromptVariable) => (
                      <div key={v.name} className="space-y-1.5">
                        <Label
                          htmlFor={`var-${v.name}`}
                          className="text-sm font-medium flex items-center gap-1.5"
                        >
                          <span className="font-mono">{v.name}</span>
                          {v.required && (
                            <span className="text-destructive text-xs font-normal">(required)</span>
                          )}
                        </Label>
                        {v.description && (
                          <p className="text-xs text-muted-foreground">{v.description}</p>
                        )}
                        <Input
                          id={`var-${v.name}`}
                          value={testVariables[v.name] ?? ''}
                          onChange={(e) =>
                            handleVariableChange(
                              setTestVariables,
                              v.name,
                              e.target.value
                            )
                          }
                          placeholder={
                            v.default != null
                              ? String(v.default)
                              : hasVariables ? `Enter ${v.name}` : ''
                          }
                          className="font-mono"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Button
                  onClick={handleRun}
                  disabled={isRunning}
                  size="lg"
                >
                  {isRunning ? (
                    <>
                      <BrandedSpinner className="mr-2" />
                      Running…
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Run prompt
                    </>
                  )}
                </Button>
                {!hasVariables && (
                  <p className="text-sm text-muted-foreground">
                    This prompt has no parameters. Click Run to execute.
                  </p>
                )}
              </div>

              {runError && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
                  <span className="shrink-0">Error:</span>
                  <span>{runError}</span>
                </div>
              )}

              {outputText !== null && (
                <div className="space-y-2 pt-2 border-t">
                  <Label className="text-sm font-medium">Output</Label>
                  <pre className="rounded-lg border bg-muted/50 p-4 text-sm overflow-x-auto max-h-[400px] overflow-y-auto whitespace-pre-wrap font-mono">
                    {outputText}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="write-test" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Write & test prompt
              </CardTitle>
              <CardDescription>
                Write or paste your own prompt text below and run it to see the model output. Your text is sent as the user prompt when the backend supports it.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-1.5">
                <Label htmlFor="custom-prompt" className="text-sm font-medium">Your prompt</Label>
                <Textarea
                  id="custom-prompt"
                  value={customPromptText}
                  onChange={(e) => setCustomPromptText(e.target.value)}
                  placeholder="Enter or paste the prompt you want to test..."
                  className="min-h-[200px] font-mono text-sm resize-y"
                  rows={8}
                />
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Button
                  onClick={handleRunCustomPrompt}
                  disabled={isCustomRunning}
                  size="lg"
                >
                  {isCustomRunning ? (
                    <>
                      <BrandedSpinner className="mr-2" />
                      Running…
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Run prompt
                    </>
                  )}
                </Button>
                <p className="text-sm text-muted-foreground">
                  Run with your custom text to test the model response.
                </p>
              </div>

              {customRunError && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                  {customRunError}
                </div>
              )}

              {customOutputText !== null && (
                <div className="space-y-2 pt-2 border-t">
                  <Label className="text-sm font-medium">Output</Label>
                  <pre className="rounded-lg border bg-muted/50 p-4 text-sm overflow-x-auto max-h-[400px] overflow-y-auto whitespace-pre-wrap font-mono">
                    {customOutputText}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="test-with-user" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Test with user context
              </CardTitle>
              <CardDescription>
                Run this prompt as if for a specific user. Enter their user ID or email to get personalized output.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-1.5">
                <Label htmlFor="user-identifier" className="text-sm font-medium">User ID or email</Label>
                <Input
                  id="user-identifier"
                  value={userIdentifier}
                  onChange={(e) => setUserIdentifier(e.target.value)}
                  placeholder="e.g. user@example.com or user-id-123"
                  className="font-mono max-w-md"
                />
              </div>

              {hasVariables && (
                <div className="space-y-3 pt-2 border-t">
                  <Label className="text-sm font-medium">Parameters (optional)</Label>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {variables.map((v: PromptVariable) => (
                      <div key={`user-${v.name}`} className="space-y-1.5">
                        <Label htmlFor={`user-var-${v.name}`} className="text-sm font-mono">
                          {v.name}
                        </Label>
                        <Input
                          id={`user-var-${v.name}`}
                          value={userVariables[v.name] ?? ''}
                          onChange={(e) =>
                            handleVariableChange(
                              setUserVariables,
                              v.name,
                              e.target.value
                            )
                          }
                          placeholder={`Value for ${v.name}`}
                          className="font-mono"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Button
                  onClick={handleRunWithUser}
                  disabled={isUserRunning}
                  size="lg"
                  variant="secondary"
                >
                  {isUserRunning ? (
                    <>
                      <BrandedSpinner className="mr-2" />
                      Running…
                    </>
                  ) : (
                    <>
                      <User className="mr-2 h-4 w-4" />
                      Run with user
                    </>
                  )}
                </Button>
              </div>

              {userRunError && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                  {userRunError}
                </div>
              )}

              {userOutputText !== null && (
                <div className="space-y-2 pt-2 border-t">
                  <Label className="text-sm font-medium">Output</Label>
                  <pre className="rounded-lg border bg-muted/50 p-4 text-sm overflow-x-auto max-h-[400px] overflow-y-auto whitespace-pre-wrap font-mono">
                    {userOutputText}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="resync" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5" />
                Resync this prompt
              </CardTitle>
              <CardDescription>
                Pull the latest version of this prompt only from the prompt library.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="release-tag" className="text-sm font-medium">Release tag (optional)</Label>
                <Input
                  id="release-tag"
                  value={releaseTag}
                  onChange={(e) => setReleaseTag(e.target.value)}
                  placeholder="e.g. v1.2.0 or main"
                  className="font-mono max-w-md"
                />
              </div>
              <Button
                onClick={handleResyncThis}
                disabled={isResyncing || isFullResyncing}
                size="lg"
              >
                {isResyncing ? (
                  <>
                    <BrandedSpinner className="mr-2" />
                    Resyncing…
                  </>
                ) : (
                  <>
                    <Layers className="mr-2 h-4 w-4" />
                    Resync this prompt
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Resync entire library with release ID
              </CardTitle>
              <CardDescription>
                Pull the latest version of all prompts from the prompt library. Enter a release ID/tag to sync from a specific release, or leave empty for the default.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="full-resync-release-id" className="text-sm font-medium">Release ID / tag</Label>
                <Input
                  id="full-resync-release-id"
                  value={fullResyncReleaseTag}
                  onChange={(e) => setFullResyncReleaseTag(e.target.value)}
                  placeholder="e.g. v2026.02.07.153627 or main"
                  className="font-mono max-w-md"
                />
                <p className="text-xs text-muted-foreground">
                  Optional. Leave empty to use the default release.
                </p>
              </div>
              <Button
                onClick={handleFullResyncWithReleaseId}
                disabled={isResyncing || isFullResyncing}
                size="lg"
                variant="default"
              >
                {isFullResyncing ? (
                  <>
                    <BrandedSpinner className="mr-2" />
                    Resyncing entire library…
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Resync whole prompt library
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {resyncMessage && (
            <div
              className={cn(
                'rounded-lg border p-4 text-sm',
                resyncMessage.type === 'success'
                  ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                  : 'border-destructive/50 bg-destructive/10 text-destructive'
              )}
            >
              {resyncMessage.text}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PromptDetailPage;
